import { useCallback, useEffect, useState } from 'react';
import { Building2, User } from 'lucide-react';
import { apiFetch } from '../stores/authStore';

interface KeyMode {
  mode: 'org' | 'user';
  useOrgKeys: boolean;
  orgKeysAvailable: boolean;
}

/**
 * Thin status strip shown above trader views: which credential pool is
 * currently active for THIS user. Driven by /api/trader/key-mode (works for
 * traders and managers alike). Hidden silently if the request fails or the
 * user has no orgId (e.g. an org-less admin).
 */
export function KeyModeBanner() {
  const [state, setState] = useState<KeyMode | null>(null);

  const fetchMode = useCallback(async () => {
    try {
      const res = await apiFetch('/api/trader/key-mode');
      if (res.ok) setState(await res.json());
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { fetchMode(); }, [fetchMode]);

  if (!state) return null;

  const isOrg = state.mode === 'org';

  return (
    <div className={`flex items-center gap-2 border-b px-4 py-1.5 text-[11px] ${
      isOrg
        ? 'border-blue-500/20 bg-blue-500/5 text-blue-700 dark:text-blue-300'
        : 'border-amber-500/20 bg-amber-500/5 text-amber-700 dark:text-amber-300'
    }`}>
      {isOrg ? <Building2 size={12} /> : <User size={12} />}
      <span>
        Trading via <strong>{isOrg ? 'ORG keys' : 'personal keys'}</strong>
        {state.useOrgKeys && !state.orgKeysAvailable && (
          <span className="ml-1 opacity-80">
            (org has no keys; falling back to personal)
          </span>
        )}
      </span>
    </div>
  );
}
